/**
 * PATH: packages/ui/src/kanban/components/card-detail/CommentNoteRow.tsx
 * FILE_ID: UI-KANBAN-COMMENT-NOTE-ROW-001
 * PURPOSE: One card-note row — author/timestamp, body, owner-only edit + delete.
 * WHY: Card notes had delete + an `edited_at` label but no left-click edit
 *   path even though PATCH /comments/<id>/ already stamps edited_at
 *   (operator 2026-07-16). Hover used a bare Lucide `X` (reads as close);
 *   now Pencil + Trash2 (Team-chat parity). Edit/Delete are OWNER-ONLY
 *   (operator 2026-07-16: "only visible to the person who wrote that note")
 *   — gated on the server's authoritative `is_own` flag
 *   (BUG-2026-07-16-KANBAN-CARD-NOTES-OWNER-ONLY-EDIT). Fall back to a
 *   client author===currentUserId compare only when `is_own` is absent
 *   (payload from a backend that predates the flag). The BE also enforces
 *   author-only PATCH/DELETE (403), so this is UX, not the security border.
 * RELATED: CommentsSection.tsx; CardTeamsThreadMessage.tsx; api updateComment.
 */
import { Check, Loader2, Pencil, Trash2 } from 'lucide-react'
import { useState, type KeyboardEvent } from 'react'

import type { KanbanMember } from '../../api'
import type { CardCommentT } from '../../types'
import { CommentBody } from '../comment-text'
import { formatDateTime } from './shared'
import { resolveCommentAuthorLabel } from './commentAuthor'

export interface CommentNoteRowProps {
  comment: CardCommentT
  members: KanbanMember[]
  resolveMember: (id: number) => KanbanMember | undefined
  currentUserId?: string | number | null
  onEdit: (commentId: string, body: string) => Promise<void>
  onDelete: (commentId: string) => void
  deletePending?: boolean
}

function sameUserId(
  author: number | null,
  currentUserId?: string | number | null,
): boolean {
  if (author == null || currentUserId == null || currentUserId === '') return false
  return String(author) === String(currentUserId)
}

export function CommentNoteRow({
  comment: c,
  members,
  resolveMember,
  currentUserId,
  onEdit,
  onDelete,
  deletePending,
}: CommentNoteRowProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(c.body)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Prefer the server's authoritative flag (Teams-thread parity); only
  // guess from the client session id when the payload predates `is_own`.
  const isOwn =
    typeof c.is_own === 'boolean' ? c.is_own : sameUserId(c.author, currentUserId)

  const startEdit = () => {
    setDraft(c.body)
    setError(null)
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
    setError(null)
  }

  const saveEdit = async () => {
    const next = draft.trim()
    if (!next || saving) return
    if (next === c.body.trim()) {
      setEditing(false)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onEdit(c.id, next)
      setEditing(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save edit')
    } finally {
      setSaving(false)
    }
  }

  const onEditorKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void saveEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEdit()
    }
  }

  return (
    <li
      data-kanban-comment-id={c.id}
      data-kanban-comment-own={isOwn ? '1' : '0'}
      className="group/c rounded-md border border-neutral-100 dark:border-neutral-900 bg-neutral-50 dark:bg-neutral-900/50 px-2 py-1.5 text-xs"
    >
      <div className="mb-0.5 flex items-center justify-between gap-2 text-[10px] text-neutral-500">
        <span>
          {resolveCommentAuthorLabel(c, resolveMember)}
          {c.edited_at ? ' · edited' : ''}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          <span className="font-mono">{formatDateTime(c.created_at)}</span>
          {isOwn && !editing && (
            <button
              type="button"
              onClick={startEdit}
              aria-label="Edit note"
              title="Edit note"
              className="rounded p-0.5 text-neutral-400 opacity-0 transition hover:bg-blue-50 hover:text-blue-600 focus:opacity-100 group-hover/c:opacity-100 dark:hover:bg-blue-950/30"
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}
          {isOwn && (
            <button
              type="button"
              onClick={() => {
                if (confirm('Delete this note?')) onDelete(c.id)
              }}
              disabled={deletePending}
              aria-label="Delete note"
              title="Delete note"
              className="rounded p-0.5 text-neutral-400 opacity-0 transition hover:bg-red-50 hover:text-red-600 focus:opacity-100 group-hover/c:opacity-100 disabled:opacity-50 dark:hover:bg-red-950/30"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onEditorKeyDown}
            rows={2}
            autoFocus
            aria-label="Edit note"
            className="w-full resize-none rounded-md border border-neutral-200 bg-white px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:border-neutral-800 dark:bg-neutral-900"
          />
          <div className="mt-1 flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => void saveEdit()}
              disabled={!draft.trim() || saving}
              className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
              ) : (
                <Check className="h-3 w-3" />
              )}
              Save
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="rounded-md px-2 py-0.5 text-[11px] font-medium text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              Cancel
            </button>
            {error && <span className="text-[10px] text-red-500">{error}</span>}
          </div>
        </div>
      ) : (
        <div className="text-neutral-800 dark:text-neutral-100">
          <CommentBody text={c.body} members={members} />
        </div>
      )}
    </li>
  )
}
